/** Slack-style number shortcuts for the desktop main rail: modifier+1..9 opens the first nine
 *  visible items, and holding the modifier for a moment shows each item's number.
 *
 *  The browser uses Ctrl because Cmd+1..9 belongs to the browser's tab switching. The desktop
 *  (Tauri) shell owns its keys, so it takes the platform's own modifier: Cmd on macOS, Ctrl on
 *  Windows. Gated to the desktop frame (desktopFrame.ts), the same condition the rail's desktop styling uses. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isTauriRuntime } from '../tauri/nativeShell';
import { useDesktopFrame } from './desktopFrame';

export const MAIN_RAIL_SHORTCUT_LIMIT = 9;
export const MAIN_RAIL_HINT_DELAY_MS = 300;

export type MainRailShortcutModifier = 'ctrl' | 'meta';

export interface MainRailShortcutItem {
  id: string;
  onSelect: () => void;
}

export interface MainRailShortcut {
  number: number;
  /** Shown in the tooltip, e.g. `Ctrl+3` or `⌘3`. */
  label: string;
  /** For `aria-keyshortcuts`, e.g. `Control+3`. */
  ariaKeyShortcuts: string;
}

export function mainRailShortcutModifier(env: { desktopShell: boolean; mac: boolean }): MainRailShortcutModifier {
  return env.desktopShell && env.mac ? 'meta' : 'ctrl';
}

function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
    ?? navigator.platform
    ?? '';
  return /mac/i.test(platform) || /Macintosh|Mac OS X/i.test(navigator.userAgent);
}

export function currentMainRailShortcutModifier(): MainRailShortcutModifier {
  return mainRailShortcutModifier({ desktopShell: isTauriRuntime(), mac: isMacPlatform() });
}

export function describeMainRailShortcut(number: number, modifier: MainRailShortcutModifier): MainRailShortcut {
  return modifier === 'meta'
    ? { number, label: `⌘${number}`, ariaKeyShortcuts: `Meta+${number}` }
    : { number, label: `Ctrl+${number}`, ariaKeyShortcuts: `Control+${number}` };
}

const EDITABLE = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';
// Custom overlays only render while open, so any visible one counts. Listboxes are often
// permanent list widgets, so only an open Radix one blocks. Shared popovers are matched by
// their slot as well, since a caller can override the dialog role Radix gives them; tooltips
// deliberately are not.
const OVERLAY = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"][data-state="open"], dialog[open], [data-slot="popover-content"]';
/** Surfaces that claim some modifier+digit chords for themselves while hovered or fullscreen,
 *  listed space-separated in the value (the PDF viewer's Ctrl/Cmd+1 actual size is `"1"`). */
export const NUMBER_KEY_OWNER_ATTR = 'data-owns-number-keys';

function isShown(el: Element): boolean {
  if (el.getAttribute('data-state') === 'closed') return false;
  if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  const check = (el as Element & { checkVisibility?: () => boolean }).checkVisibility;
  return typeof check === 'function' ? check.call(el) : true;
}

function numberKeyOwnedElsewhere(doc: Document, digit: number): boolean {
  const owner = `[${NUMBER_KEY_OWNER_ATTR}~="${digit}"]`;
  if (doc.fullscreenElement?.closest(owner) || doc.fullscreenElement?.querySelector(owner)) return true;
  try {
    return doc.querySelector(`${owner}:hover`) !== null;
  } catch {
    return false;
  }
}

/** Pass `digit` to also check whether another surface owns that particular chord. */
export function mainRailShortcutsBlocked(doc: Document = document, digit?: number): boolean {
  const active = doc.activeElement;
  if (active instanceof HTMLElement && (active.isContentEditable || active.closest(EDITABLE))) return true;
  if (digit !== undefined && numberKeyOwnedElsewhere(doc, digit)) return true;
  return Array.from(doc.querySelectorAll(OVERLAY)).some(isShown);
}

/** The digit 1..9 a key event names, read from the physical key so non-UK layouts still match. */
function shortcutDigit(event: KeyboardEvent): number | null {
  const fromCode = /^Digit([1-9])$/.exec(event.code ?? '');
  if (fromCode) return Number(fromCode[1]);
  return /^[1-9]$/.test(event.key) ? Number(event.key) : null;
}

function onlyModifier(event: KeyboardEvent, modifier: MainRailShortcutModifier): boolean {
  if (event.altKey || event.shiftKey) return false;
  return modifier === 'meta' ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
}

export function useMainRailShortcuts(items: MainRailShortcutItem[], railShown: boolean) {
  const inFrame = useDesktopFrame();
  const enabled = railShown && inFrame;
  const [hintVisible, setHintVisible] = useState(false);
  const [modifier] = useState(currentMainRailShortcutModifier);
  // Read at key time, so the listener never re-binds as the rail re-renders.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    if (!enabled) return;
    const modifierKey = modifier === 'meta' ? 'Meta' : 'Control';
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cancelTimer = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    };
    const hide = () => {
      cancelTimer();
      setHintVisible(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === modifierKey) {
        if (event.repeat || timer !== undefined) return;
        if (!onlyModifier(event, modifier) || mainRailShortcutsBlocked()) return;
        timer = setTimeout(() => {
          timer = undefined;
          setHintVisible(true);
        }, MAIN_RAIL_HINT_DELAY_MS);
        return;
      }
      // Any other key while held is a chord (Ctrl+C and friends), so the hint stays away.
      cancelTimer();
      const digit = shortcutDigit(event);
      if (digit === null || event.repeat || event.isComposing || !onlyModifier(event, modifier)) return;
      if (mainRailShortcutsBlocked(document, digit)) return;
      const item = itemsRef.current.slice(0, MAIN_RAIL_SHORTCUT_LIMIT)[digit - 1];
      if (!item) return;
      event.preventDefault();
      hide();
      item.onSelect();
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === modifierKey) hide();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') hide();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', hide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', hide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      hide();
    };
  }, [enabled, modifier]);

  const shortcutIdsKey = items.slice(0, MAIN_RAIL_SHORTCUT_LIMIT).map((item) => item.id).join('\n');
  const shortcutsById = useMemo(() => {
    const map = new Map<string, MainRailShortcut>();
    if (!enabled || !shortcutIdsKey) return map;
    shortcutIdsKey.split('\n').forEach((id, index) => map.set(id, describeMainRailShortcut(index + 1, modifier)));
    return map;
  }, [enabled, shortcutIdsKey, modifier]);
  const shortcutFor = useCallback((id: string) => shortcutsById.get(id), [shortcutsById]);

  return { hintVisible: enabled && hintVisible, shortcutFor };
}
